/**
 * The one place a drag result becomes a card mutation (ADR-003).
 *
 * Keeping every `DropResult` translation here means the drag-and-drop library
 * sits behind a seam of its own: the reducer only ever sees intent-shaped
 * actions ("pair these two", "anchor this on the photo"), and swapping the
 * library out would not touch `state/`.
 */

import { useCallback, useState } from 'react';
import { DragStart, DragUpdate, DropResult } from '@hello-pangea/dnd';

import {
  CARD_BLOCK_DND_TYPE,
  FLOATING_DROPPABLE_ID,
  IMAGE_FIELD_ID,
  INFO_GAP_PREFIX,
  INFO_ROW_PREFIX,
  PHOTO_PREFIX,
  RAIL_DROPPABLE_ID,
  isPhotoAnchor,
  parseDraggableFieldId,
  parseLaneIndex,
  regionForDroppable,
} from '../constants';
import { selectInfoRows } from '../selectors';
import { CardAction, cardActions } from '../state';
import { FieldId, ProductCard, RegionId } from '../types';

interface UseBlockDndResult {
  /**
   * True while a CARD BLOCK is being dragged — the preview draws its drop hints from this.
   * A rail (field-list) reorder does not set it: rail rows are `RAIL_DND_TYPE` and every
   * preview droppable accepts only `CARD_BLOCK_DND_TYPE`, so there is nowhere on the card a
   * rail row could land.
   */
  isDragging: boolean;
  /** The region a drop would land in right now, for the highlight (spec R49). */
  activeRegion: RegionId | null;
  onDragStart: (start: DragStart) => void;
  onDragUpdate: (update: DragUpdate) => void;
  onDragEnd: (result: DropResult) => void;
}

export const useBlockDnd = (
  /** Null while the card is still loading; every handler then no-ops. */
  card: ProductCard | null,
  dispatch: (action: CardAction) => void,
  /** Rail rows as currently rendered — the field search may have filtered them. */
  visibleRailFieldIds: FieldId[]
): UseBlockDndResult => {
  const [isDragging, setIsDragging] = useState(false);
  const [activeRegion, setActiveRegion] = useState<RegionId | null>(null);

  /*
   * Only a card-block drag lights the preview. Reordering the field list used to set
   * `isDragging` too, so the photo's TL/TR/Center/BL/BR/Bottom-strip zones, the footer's
   * "drag items here" and the info lanes all appeared as if the row could be dropped on the
   * card — it cannot (different drag type), so every hint was a promise the drop would break.
   * The rail reorder itself is unchanged: `onDragEnd` below still handles it.
   */
  const onDragStart = useCallback((start: DragStart) => {
    if (start.type !== CARD_BLOCK_DND_TYPE) return;
    setIsDragging(true);
    setActiveRegion(regionForDroppable(start.source.droppableId));
  }, []);

  const onDragUpdate = useCallback((update: DragUpdate) => {
    if (update.type !== CARD_BLOCK_DND_TYPE) return;
    setActiveRegion(
      update.destination ? regionForDroppable(update.destination.droppableId) : null
    );
  }, []);

  const onDragEnd = useCallback(
    (result: DropResult) => {
      setIsDragging(false);
      setActiveRegion(null);

      const fieldId = parseDraggableFieldId(result.draggableId);
      const destination = result.destination;
      if (!card || !fieldId || !destination) {
        return;
      }

      // Rail reorder. `destination.index` indexes the list *after* the dragged
      // row is lifted out, so the field lands before whatever now sits there —
      // which stays correct while a search filter is narrowing the list.
      if (result.source.droppableId === RAIL_DROPPABLE_ID) {
        if (destination.droppableId !== RAIL_DROPPABLE_ID) {
          return;
        }
        const remaining = visibleRailFieldIds.filter((candidate) => candidate !== fieldId);
        /*
          The Primary Image is pinned to index 0 (`completeFieldOrder`), so a drop
          that would land ABOVE it is not a legal position. Landing the row just
          after the image instead makes the drop do the nearest legal thing —
          without this the reducer still refuses, but the row visibly springs back
          past the image, which reads as a broken drag rather than a rule.
        */
        const before =
          remaining[destination.index] === IMAGE_FIELD_ID
            ? remaining[destination.index + 1] ?? null
            : remaining[destination.index] ?? null;
        dispatch(cardActions.moveField(fieldId, before));
        return;
      }

      if (destination.droppableId === FLOATING_DROPPABLE_ID) {
        dispatch(cardActions.moveBlockToFloating(fieldId));
        return;
      }

      if (destination.droppableId.startsWith(PHOTO_PREFIX)) {
        const anchor = destination.droppableId.slice(PHOTO_PREFIX.length);
        if (isPhotoAnchor(anchor)) {
          dispatch(cardActions.moveBlockToPhoto(fieldId, anchor));
        }
        return;
      }

      if (destination.droppableId.startsWith(INFO_GAP_PREFIX)) {
        dispatch(
          cardActions.moveBlockToInfoRow(
            fieldId,
            parseLaneIndex(destination.droppableId, INFO_GAP_PREFIX)
          )
        );
        return;
      }

      if (destination.droppableId.startsWith(INFO_ROW_PREFIX)) {
        const rowIndex = parseLaneIndex(destination.droppableId, INFO_ROW_PREFIX);
        const row = selectInfoRows(card)[rowIndex];
        if (!row) {
          return;
        }
        // Dropping into a row that only held the dragged block is a plain
        // reorder, not a pairing.
        const peers = row.placements.filter((placement) => placement.fieldId !== fieldId);
        if (peers.length === 0) {
          dispatch(cardActions.moveBlockToInfoRow(fieldId, rowIndex));
          return;
        }
        if (destination.index <= 0) {
          dispatch(cardActions.pairBlock(fieldId, peers[0].fieldId, 'left'));
          return;
        }
        const peer = peers[Math.min(destination.index, peers.length) - 1];
        dispatch(cardActions.pairBlock(fieldId, peer.fieldId, 'right'));
      }
    },
    [card, dispatch, visibleRailFieldIds]
  );

  return { isDragging, activeRegion, onDragStart, onDragUpdate, onDragEnd };
};
